import React, { useState, useCallback, memo, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Autocomplete,
  Paper,
  IconButton,
  Button
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { supabase } from '../../supabaseClient'; // Assuming supabase client is needed here for characteristics updates
import debounce from 'lodash/debounce'; // Import debounce
import { v4 as uuidv4 } from 'uuid'; // Import uuid for generating new characteristic IDs

// Standardized AutocompleteField (copied from DrillPointsTab.jsx)
const AutocompleteField = memo(({ 
  label, 
  value, // expects domain_value_id directly
  onChange, // expects function(newValueId)
  options, // expects array of { domain_value_id, description, ... }
  loading,
  keyPrefix,
  isReadOnly
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  
  // Sort options alphabetically, except for unit fields
  const sortedOptions = useMemo(() => {
    if (!options || !Array.isArray(options)) return [];
    
    // Don't sort if the label contains "eenheid" (case insensitive)
    const isUnitField = label.toLowerCase().includes('eenheid');
    
    if (isUnitField) {
      return options;
    }
    
    return [...options].sort((a, b) => 
      (a.description || '').localeCompare(b.description || '')
    );
  }, [options, label]);

  const filteredOptions = useMemo(() => {
    if (!sortedOptions || !Array.isArray(sortedOptions)) return [];
    if (!inputValue || inputValue.length < 1) {
      return sortedOptions;
    }
    return sortedOptions.filter(option => 
      option.description?.toLowerCase().includes(inputValue.toLowerCase())
    );
  }, [inputValue, sortedOptions]);

  const currentOption = useMemo(() => {
    const found = (options || []).find(option => option.domain_value_id === value);
    return found || null;
  }, [options, value]);
  
  const handleInputChange = useCallback(debounce((event, newInputValue, reason) => {
    if (reason === 'input' || reason === 'clear') {
        setInputValue(newInputValue);
    } 
  }, 50), []);
  
  if (!options || !Array.isArray(options) || options.length === 0) { // Make check more robust
    return (
      <TextField
        fullWidth
        label={label}
        value=""
        disabled
        margin="normal"
        helperText={`Geen opties beschikbaar voor ${label}`}
      />
    );
  }

  return (
    <Autocomplete
      fullWidth
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={filteredOptions} 
      getOptionLabel={(option) => option.description || ''} 
      isOptionEqualToValue={(option, val) => option.domain_value_id === val?.domain_value_id}
      value={currentOption} 
      onChange={(event, newValue) => {
        // Call original onChange for database update
        onChange(newValue?.domain_value_id || null); 
      }}
      disabled={isReadOnly}
      renderOption={(liProps, option) => { 
        const { key, ...restLiProps } = liProps;
        return (
          <li
            key={key}
            {...restLiProps}
            style={{
              ...restLiProps.style,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block'
            }}
          >
            {option.description || ''}
          </li>
        );
      }}
      onInputChange={handleInputChange} 
      renderInput={(params) => (
        <TextField 
          {...params} 
          label={label} 
          fullWidth
          margin="normal"
        />
      )}
      loading={loading && filteredOptions.length === 0}
      loadingText="Opties laden..."
      noOptionsText="Geen resultaten"
    />
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.value === nextProps.value &&
    prevProps.loading === nextProps.loading &&
    prevProps.options === nextProps.options 
  );
});

// NOTE: Need to receive 'allCharacteristics' and 'setCharacteristics' from LayerInfo
function LayerDetails({ 
  layer, 
  domainValues, // Receives the combined domain values
  referentieVlakData,
  referentieVlakLoading,
  characteristicsLoading,
  domainValuesLoading,
  saving,
  onFieldChange,
  setError,
  technicalNameToIndicatorId,
  onCharacteristicUpdate,
  allCharacteristics, // Prop containing characteristics for the whole document
  layerCharacteristics, // Receive filtered list for THIS layer
  setCharacteristics, // Function to update the main characteristics state in LayerInfo
  lengthUnitsData,     
  lengthUnitsLoading,  
  onAddCharacteristicPair,   
  onDeleteCharacteristicPair,
  technicalNameToDomain, // Pass this down
  isReadOnly // Add isReadOnly
}) {

  const [bijzonderheden, setBijzonderheden] = useState([]);

  const BIJZONDERHEID_NAAM_INDICATOR = useMemo(() => Object.keys(technicalNameToIndicatorId).find(key => technicalNameToIndicatorId[key] === '52b97ef9-e18a-4135-a1b2-8dcfbbf81b39'), [technicalNameToIndicatorId]);
  const BIJZONDERHEID_GRADATIE_INDICATOR = useMemo(() => Object.keys(technicalNameToIndicatorId).find(key => technicalNameToIndicatorId[key] === 'ec52be58-4a33-426d-a18f-68bda1aae63c'), [technicalNameToIndicatorId]);
  
  useEffect(() => {
    if (!layerCharacteristics || characteristicsLoading || !BIJZONDERHEID_NAAM_INDICATOR || !BIJZONDERHEID_GRADATIE_INDICATOR) {
      setBijzonderheden([]);
      return;
    }

    const naamIndicatorId = technicalNameToIndicatorId[BIJZONDERHEID_NAAM_INDICATOR];
    const gradatieIndicatorId = technicalNameToIndicatorId[BIJZONDERHEID_GRADATIE_INDICATOR];

    const naamCharacteristics = layerCharacteristics.filter(char => char.indicator === naamIndicatorId);
    const gradatieCharacteristics = layerCharacteristics.filter(char => char.indicator === gradatieIndicatorId);

    const groupedBijzonderheden = naamCharacteristics.map(naamChar => {
      const gradatieChar = gradatieCharacteristics.find(gradChar => gradChar.relatedObservation === naamChar.id);
      return {
        id: naamChar.id, 
        naam: naamChar,      
        gradatie: gradatieChar 
      };
    });

    setBijzonderheden(groupedBijzonderheden);

  }, [layerCharacteristics, characteristicsLoading, technicalNameToIndicatorId, BIJZONDERHEID_NAAM_INDICATOR, BIJZONDERHEID_GRADATIE_INDICATOR]);

  const renderField = useCallback((currentLayer, fieldName, technicalName, label, fieldType = 'autocomplete', overrideValue = undefined, overrideOnChange = undefined, characteristicObject = null) => {
    
    const actualValue = characteristicObject ? characteristicObject.classifiedResult : (overrideValue !== undefined ? overrideValue : currentLayer[fieldName]);
    const actualOnChange = characteristicObject 
        ? (newValueId) => onCharacteristicUpdate(currentLayer.id, currentLayer.document_id, { 
            id: characteristicObject.id, 
            indicator: characteristicObject.indicator,
            technicalName: technicalName, 
            relatedObservation: characteristicObject.relatedObservation
          }, newValueId)
        : (overrideOnChange ? overrideOnChange : (newValue) => {
            onFieldChange(currentLayer, fieldName, newValue);
          });

    if (fieldType === 'autocomplete' && technicalName) {
        let options = domainValues[technicalName] || [];

        return (
            <AutocompleteField
                key={`${characteristicObject ? characteristicObject.id : currentLayer.id}-${technicalName}-${fieldName}`}
                label={label}
                value={actualValue} 
                onChange={actualOnChange} 
                options={options}
                loading={domainValuesLoading || saving || characteristicsLoading} 
                keyPrefix={`${fieldName}-${characteristicObject ? characteristicObject.id : currentLayer.id}`}
                isReadOnly={isReadOnly}
            />
        );
    }

    return (
        <TextField
            key={`${currentLayer.id}-${fieldName}`}
            fullWidth
            label={label}
            margin="normal"
            type={fieldType === 'number' ? 'number' : 'text'}
            value={actualValue || ''} 
            onChange={(e) => actualOnChange(e.target.value)} 
            inputProps={fieldType === 'number' ? { step: "0.01" } : {}}
            disabled={isReadOnly}
        />
    );

  }, [domainValues, domainValuesLoading, saving, onFieldChange, layer.id, layer.document_id, onCharacteristicUpdate, characteristicsLoading, isReadOnly]);

  const renderDomainValueFields = useCallback((currentLayer) => {
    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            sx={{ flex: '0 0 30%' }}
            label="Bovendiepte"
            margin="normal"
            type="number"
            value={currentLayer.upperdepth === null || currentLayer.upperdepth === '' ? '0' : currentLayer.upperdepth}
            onChange={(e) => onFieldChange(currentLayer, 'upperdepth', e.target.value)}
            inputProps={{ step: "0.01" }}
            disabled={isReadOnly}
          />
          <Box sx={{ flex: '0 0 70%' }}>
            <AutocompleteField
              label="Eenheid"
              value={currentLayer.upperdepth_uom}
              onChange={(newValue) => onFieldChange(currentLayer, 'upperdepth_uom', newValue)}
              options={lengthUnitsData?.['Eenheid'] || []}
              loading={lengthUnitsLoading}
              keyPrefix={`upper-uom-${currentLayer.id}`}
              isReadOnly={isReadOnly}
            />
          </Box>
        </Box>
        
        <AutocompleteField
            label="Bovendiepte conditie"
            value={currentLayer.upperdepth_condition}
            onChange={(newValue) => onFieldChange(currentLayer, 'upperdepth_condition', newValue)}
            options={referentieVlakData?.['Hoedanigheid'] || []}
            loading={referentieVlakLoading}
            keyPrefix={`upper-cond-${currentLayer.id}`}
            isReadOnly={isReadOnly}
        />
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            sx={{ flex: '0 0 30%' }}
            label="Onderdiepte"
            margin="normal"
            type="number"
            value={currentLayer.lowerdepth || ''}
            onChange={(e) => onFieldChange(currentLayer, 'lowerdepth', e.target.value)}
            inputProps={{ step: "0.01" }}
            disabled={isReadOnly}
          />
          <Box sx={{ flex: '0 0 70%' }}>
            <AutocompleteField
              label="Eenheid"
              value={currentLayer.lowerdepth_uom}
              onChange={(newValue) => onFieldChange(currentLayer, 'lowerdepth_uom', newValue)}
              options={lengthUnitsData?.['Eenheid'] || []}
              loading={lengthUnitsLoading}
              keyPrefix={`lower-uom-${currentLayer.id}`}
              isReadOnly={isReadOnly}
            />
          </Box>
        </Box>
        
        <AutocompleteField
            label="Onderdiepte conditie"
            value={currentLayer.lowerdepth_condition}
            onChange={(newValue) => onFieldChange(currentLayer, 'lowerdepth_condition', newValue)}
            options={referentieVlakData?.['Hoedanigheid'] || []}
            loading={referentieVlakLoading}
            keyPrefix={`lower-cond-${currentLayer.id}`}
            isReadOnly={isReadOnly}
        />
        
        {renderField(currentLayer, 'layer_type', 'BodemlaagType', 'Type laag', 'autocomplete', undefined, undefined, null)}
        {renderField(currentLayer, 'samplingdevice', 'Bemonsteringsapparaat', 'Boorapparaat', 'autocomplete', undefined, undefined, null)}
      </Box>
    );
  }, [renderField, onFieldChange, lengthUnitsData, lengthUnitsLoading, referentieVlakData, referentieVlakLoading, isReadOnly]);

  const getCharacteristicValue = useCallback((technicalName) => {
      const indicatorId = technicalNameToIndicatorId[technicalName];
      if (!indicatorId) {
          return null;
      }
      const characteristic = layerCharacteristics.find(char => char.indicator === indicatorId);
      return characteristic ? characteristic.classifiedResult : null;
  }, [layerCharacteristics, technicalNameToIndicatorId]);

  const handleAddBijzonderheid = useCallback(() => {
      if (isReadOnly || !BIJZONDERHEID_NAAM_INDICATOR || !BIJZONDERHEID_GRADATIE_INDICATOR || !onAddCharacteristicPair) return;

      const naamIndicatorId = technicalNameToIndicatorId[BIJZONDERHEID_NAAM_INDICATOR];
      const gradatieIndicatorId = technicalNameToIndicatorId[BIJZONDERHEID_GRADATIE_INDICATOR];

      const newNaamId = uuidv4();
      const newGradatieId = uuidv4(); 

      const newNaamCharacteristic = {
          id: newNaamId,
          indicator: naamIndicatorId,
          classifiedResult: null,
          link_id: layer.id,
          document_id: layer.document_id, 
      };
      const newGradatieCharacteristic = {
          id: newGradatieId,
          indicator: gradatieIndicatorId,
          classifiedResult: null, 
          relatedObservation: newNaamId, 
          link_id: layer.id,
          document_id: layer.document_id,
      };
      
      setBijzonderheden(prev => [
          ...prev,
          { id: newNaamId, naam: newNaamCharacteristic, gradatie: newGradatieCharacteristic }
      ]);

      onAddCharacteristicPair(layer.id, layer.document_id, {
          naam: { indicator: naamIndicatorId, id: newNaamId }, 
          gradatie: { indicator: gradatieIndicatorId, id: newGradatieId, relatedObservation: newNaamId }
      });

  }, [layer.id, layer.document_id, BIJZONDERHEID_NAAM_INDICATOR, BIJZONDERHEID_GRADATIE_INDICATOR, technicalNameToIndicatorId, onAddCharacteristicPair, setBijzonderheden, isReadOnly]);

  const handleDeleteBijzonderheid = useCallback((naamCharId, gradatieCharId) => {
      if (isReadOnly || !onDeleteCharacteristicPair) return;
      
      setBijzonderheden(prev => prev.filter(pair => pair.id !== naamCharId));
      onDeleteCharacteristicPair(naamCharId, gradatieCharId); 

  }, [onDeleteCharacteristicPair, setBijzonderheden, isReadOnly]);

  // --- Function to handle characteristic updates (Corrected placement) --- 
  const handleCharacteristicUpdate = useCallback(async (layerId, documentId, fieldInfo, newValueId) => {
    if (isReadOnly) return;
    setSaving(true);
    setError(null);

    const { technicalName } = fieldInfo;
    const indicatorToUse = technicalNameToIndicatorId[technicalName];
    const domainToUse = technicalNameToDomain[technicalName]; // Get domain URN

    let hoofdkleurId = null;
    const hoofdkleurIndicatorId = technicalNameToIndicatorId['Hoofdkleur'];

    if (technicalName === 'Bijkleur' || technicalName === 'Kleursterkte') {
      const hoofdkleurChar = allCharacteristics.find( // Use allCharacteristics here
        char => char.link_id === layerId && char.indicator === hoofdkleurIndicatorId
      );
      if (hoofdkleurChar) {
        hoofdkleurId = hoofdkleurChar.id;
      } 
    }

    if (!indicatorToUse) {
        setError(`Fout: Kon indicator ID niet bepalen voor ${technicalName}.`);
        setSaving(false);
        return;
    }

    try {
      const existingChar = allCharacteristics.find( // Use allCharacteristics here
        char => char.link_id === layerId && char.indicator === indicatorToUse
      );

      if (existingChar) {
        if (newValueId === null || newValueId === undefined || newValueId === '') {
          // Delete characteristic
          await deleteCharacteristic(existingChar.id);
          // Update main state in LayerInfo
          setCharacteristics(prev => prev.filter(c => c.id !== existingChar.id)); 
          
          // Handle Hoofdkleur deletion side effect
          if (technicalName === 'Hoofdkleur') {
              const relatedChars = allCharacteristics.filter(
                  char => char.link_id === layerId && char.relatedObservation === existingChar.id
              );
              for (const relatedChar of relatedChars) {
                  try {
                      await updateCharacteristic(relatedChar.id, { relatedObservation: null });
                      // Update main state for related chars
                      setCharacteristics(prev => prev.map(c => c.id === relatedChar.id ? { ...c, relatedObservation: null } : c)); 
                  } catch(updateError) {
                      setError(`Fout bij bijwerken gerelateerde kleur ${relatedChar.id}: ${updateError.message}`);
                  }
              }
          }
        } else if (existingChar.classifiedResult !== newValueId || 
                   ((technicalName === 'Bijkleur' || technicalName === 'Kleursterkte') && existingChar.relatedObservation !== hoofdkleurId) ) { 
          // Update characteristic
          const updateData = {
            classifiedResult: newValueId,
            resulttime: new Date().toISOString()
          };
          if (technicalName === 'Bijkleur' || technicalName === 'Kleursterkte') {
              updateData.relatedObservation = hoofdkleurId;
          }

          const updatedChar = await updateCharacteristic(existingChar.id, updateData);
           // Update main state with full updated char
           setCharacteristics(prev => prev.map(c => c.id === existingChar.id ? { ...c, ...updatedChar } : c)); 
        }
      } else if (newValueId !== null && newValueId !== undefined && newValueId !== '') {
        // Create new characteristic
        if ((technicalName === 'Bijkleur' || technicalName === 'Kleursterkte') && !hoofdkleurId) {
            setError(`Kan ${technicalName} niet opslaan: Selecteer eerst een Hoofdkleur voor deze laag.`);
            setSaving(false);
            return;
        }

        const newCharId = uuidv4();
        const newCharData = {
          id: newCharId, 
          document_id: documentId,
          link_id: layerId,
          classifiedResult: newValueId,
          indicator: indicatorToUse,
          classifiedresult_domain: domainToUse, // Use determined domain URN
          procedure: 'proc_veldwaarneming',
        };
        
        if (technicalName === 'Bijkleur' || technicalName === 'Kleursterkte') {
            newCharData.relatedObservation = hoofdkleurId;
        }
        
        // Create in DB
        await createCharacteristic(newCharData);

        // Fetch the newly created characteristic with joins for state update
        const { data: fetchedNewChar, error: fetchError } = await supabase
          .from('document_characteristic')
          .select(`*,
              classified_result:document_domainvalueslookup!document_characteristic_classifiedResult_fkey(domain_value_id, description),
              indicator_info:document_domainvalueslookup!document_characteristic_indicator_fkey(domain_value_id, technical_name, description)
          `)
          .eq('id', newCharId)
          .single();

        if (fetchError || !fetchedNewChar) {
             console.error("Error fetching new char or no data:", fetchError);
             // Fallback: Add basic data (might cause UI issues if joins are expected)
             setCharacteristics(prev => [...prev, { ...newCharData, classified_result: null, indicator_info: null }]);
        } else {
             // Add fully populated data to main state
             setCharacteristics(prev => [...prev, fetchedNewChar]); 
        }
      }
    } catch (err) {
      setError(`Fout bij bijwerken eigenschap: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }, [allCharacteristics, setCharacteristics, technicalNameToIndicatorId, technicalNameToDomain, isReadOnly]);

  return (
    <Paper elevation={0} sx={{ 
      p: 2, 
      ml: 4, 
      mb: 2,
      bgcolor: 'background.default',
      borderLeft: '1px solid',
      borderLeftColor: 'primary.main',
    }}>
      {renderDomainValueFields(layer)}
      
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" gutterBottom color="primary"> 
            Eigenschappen 
        </Typography>
        {domainValuesLoading || characteristicsLoading ? (
          <CircularProgress size={24} sx={{ml: 1}}/>
        ) : (
          <>
            {renderField(layer, 'char_OrganischeStofGehalte', 'OrganischeStofGehalte', 'Organische stof gehalte', 'autocomplete', getCharacteristicValue('OrganischeStofGehalte'),
              (newValueId) => onCharacteristicUpdate(layer.id, layer.document_id, { technicalName: 'OrganischeStofGehalte' }, newValueId))}
            {renderField(layer, 'char_GrindGehalte', 'GrindGehalte', 'Grind gehalte', 'autocomplete', getCharacteristicValue('GrindGehalte'),
              (newValueId) => onCharacteristicUpdate(layer.id, layer.document_id, { technicalName: 'GrindGehalte' }, newValueId))}
            {renderField(layer, 'char_Bodemsoort', 'Bodemsoort', 'Bodemsoort', 'autocomplete', getCharacteristicValue('Bodemsoort'),
              (newValueId) => onCharacteristicUpdate(layer.id, layer.document_id, { technicalName: 'Bodemsoort' }, newValueId))}
            {renderField(layer, 'char_BodemsoortMediaan', 'BodemsoortMediaan', 'Korrelgrootte mediaan', 'autocomplete', getCharacteristicValue('BodemsoortMediaan'),
              (newValueId) => onCharacteristicUpdate(layer.id, layer.document_id, { technicalName: 'BodemsoortMediaan' }, newValueId))}
            
            {renderField(layer, 'char_Hoofdkleur', 'Kleur', 'Hoofdkleur', 'autocomplete', getCharacteristicValue('Hoofdkleur'),
              (newValueId) => onCharacteristicUpdate(layer.id, layer.document_id, { technicalName: 'Hoofdkleur' }, newValueId))}
            {renderField(layer, 'char_kleur', 'Kleur', 'Bijkleur', 'autocomplete', getCharacteristicValue('Bijkleur'),
              (newValueId) => onCharacteristicUpdate(layer.id, layer.document_id, { technicalName: 'Bijkleur' }, newValueId))}
            {renderField(layer, 'char_Kleursterkte', 'Kleursterkte', 'Kleursterkte', 'autocomplete', getCharacteristicValue('Kleursterkte'),
              (newValueId) => onCharacteristicUpdate(layer.id, layer.document_id, { technicalName: 'Kleursterkte' }, newValueId))}
            
            {renderField(layer, 'char_OliewaterReactieMate', 'OliewaterReactieMate', 'Oliewater reactie', 'autocomplete', getCharacteristicValue('OliewaterReactieMate'),
              (newValueId) => onCharacteristicUpdate(layer.id, layer.document_id, { technicalName: 'OliewaterReactieMate' }, newValueId))}
          </>
        )}
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" gutterBottom color="primary">
          Bijzonderheden
        </Typography>
        {domainValuesLoading || characteristicsLoading ? (
          <CircularProgress size={24} sx={{ml: 1}}/>
        ) : (
          <>
            {bijzonderheden.map((pair, index) => (
              <Box key={pair.id || index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ flexGrow: 1 }}>
                  {renderField(
                      layer,
                      `bijzonderheid_naam_${pair.id}`, 
                      BIJZONDERHEID_NAAM_INDICATOR,
                      'Naam', 
                      'autocomplete', 
                      undefined, 
                      undefined, 
                      pair.naam 
                  )}
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  {renderField(
                      layer, 
                      `bijzonderheid_gradatie_${pair.id}`, 
                      BIJZONDERHEID_GRADATIE_INDICATOR,
                      'Gradatie', 
                      'autocomplete', 
                      undefined, 
                      undefined, 
                      pair.gradatie 
                  )}
                </Box>
                 <IconButton 
                    onClick={() => handleDeleteBijzonderheid(pair.naam.id, pair.gradatie?.id)} 
                    color="error"
                    size="small"
                    sx={{ mt: 1 }}
                    disabled={isReadOnly}
                 >
                    <DeleteOutlineIcon />
                 </IconButton>
              </Box>
            ))}
            <Button
              startIcon={<AddCircleOutlineIcon />}
              onClick={handleAddBijzonderheid}
              size="small"
              sx={{ mt: 1 }}
              disabled={isReadOnly}
            >
              Bijzonderheid Toevoegen
            </Button>
          </>
        )}
      </Box>
    </Paper>
  );
}

export default memo(LayerDetails, (prevProps, nextProps) => {
  return (
    prevProps.layer === nextProps.layer &&
    prevProps.domainValues === nextProps.domainValues &&
    prevProps.characteristicsLoading === nextProps.characteristicsLoading &&
    prevProps.domainValuesLoading === nextProps.domainValuesLoading &&
    prevProps.saving === nextProps.saving &&
    prevProps.allCharacteristics === nextProps.allCharacteristics && // Add comparison for allCharacteristics
    prevProps.layerCharacteristics === nextProps.layerCharacteristics &&
    prevProps.lengthUnitsData === nextProps.lengthUnitsData &&
    prevProps.lengthUnitsLoading === nextProps.lengthUnitsLoading &&
    prevProps.onFieldChange === nextProps.onFieldChange &&
    prevProps.onCharacteristicUpdate === nextProps.onCharacteristicUpdate &&
    prevProps.onAddCharacteristicPair === nextProps.onAddCharacteristicPair && 
    prevProps.onDeleteCharacteristicPair === nextProps.onDeleteCharacteristicPair &&
    prevProps.referentieVlakData === nextProps.referentieVlakData && 
    prevProps.referentieVlakLoading === nextProps.referentieVlakLoading &&
    prevProps.setCharacteristics === nextProps.setCharacteristics && // Compare setCharacteristics
    prevProps.isReadOnly === nextProps.isReadOnly
  );
});
